const SYSTEM_KEYS = new Set(['id', 'sortOrder', 'isActive']);
const INTERNAL_KEYS = new Set(['_cloudinaryAssets', 'createdAt', 'updatedAt']);

class PortalCodeParser {
  constructor(source) {
    const cleaned = String(source || '')
      .trim()
      .replace(/^```(?:javascript|js|jsx|json)?\s*/i, '')
      .replace(/```\s*$/i, '')
      .trim();
    const start = cleaned.indexOf('{');
    if (start < 0) throw new Error('Object data { ... } malyu nathi.');
    this.source = cleaned.slice(start);
    this.index = 0;
  }

  error(message) {
    const before = this.source.slice(0, this.index);
    const line = before.split('\n').length;
    const column = this.index - before.lastIndexOf('\n');
    throw new Error(`${message} (line ${line}, column ${column})`);
  }

  peek() {
    return this.source[this.index];
  }

  skip() {
    while (this.index < this.source.length) {
      if (/\s/.test(this.peek())) {
        this.index += 1;
        continue;
      }
      if (this.source.startsWith('//', this.index)) {
        const nextLine = this.source.indexOf('\n', this.index + 2);
        this.index = nextLine < 0 ? this.source.length : nextLine + 1;
        continue;
      }
      if (this.source.startsWith('/*', this.index)) {
        const close = this.source.indexOf('*/', this.index + 2);
        if (close < 0) this.error('Comment close thayeli nathi.');
        this.index = close + 2;
        continue;
      }
      break;
    }
  }

  consume(character) {
    this.skip();
    if (this.peek() !== character) this.error(`Expected "${character}".`);
    this.index += 1;
  }

  parse() {
    const value = this.parseValue();
    this.skip();
    if (this.peek() === ';') {
      this.index += 1;
      this.skip();
    }
    if (this.index < this.source.length) this.error('Object pachhi unexpected code chhe.');
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      this.error('Root data object hovu joiye.');
    }
    return value;
  }

  parseValue() {
    this.skip();
    const character = this.peek();
    if (character === '{') return this.parseObject();
    if (character === '[') return this.parseArray();
    if (character === '"' || character === "'" || character === '`') return this.parseString();
    if (character === '-' || /\d/.test(character || '')) return this.parseNumber();
    if (/[A-Za-z_$]/.test(character || '')) return this.parseIdentifierValue();
    this.error('Value samjayu nathi.');
  }

  parseObject() {
    const value = {};
    this.consume('{');
    this.skip();
    while (this.peek() !== '}') {
      if (this.index >= this.source.length) this.error('Object close thayelo nathi.');
      const key = this.peek() === '"' || this.peek() === "'" || this.peek() === '`'
        ? this.parseString()
        : this.parseIdentifier();
      this.consume(':');
      value[key] = this.parseValue();
      this.skip();
      if (this.peek() === ',') {
        this.index += 1;
        this.skip();
        if (this.peek() === '}') break;
      } else if (this.peek() !== '}') {
        this.error('Object field pachhi comma required chhe.');
      }
    }
    this.consume('}');
    return value;
  }

  parseArray() {
    const value = [];
    this.consume('[');
    this.skip();
    while (this.peek() !== ']') {
      if (this.index >= this.source.length) this.error('Array close thayeli nathi.');
      value.push(this.parseValue());
      this.skip();
      if (this.peek() === ',') {
        this.index += 1;
        this.skip();
        if (this.peek() === ']') break;
      } else if (this.peek() !== ']') {
        this.error('Array item pachhi comma required chhe.');
      }
    }
    this.consume(']');
    return value;
  }

  parseString() {
    this.skip();
    const quote = this.peek();
    this.index += 1;
    let value = '';
    while (this.index < this.source.length) {
      const character = this.peek();
      this.index += 1;
      if (character === quote) return value;
      if (quote === '`' && character === '$' && this.peek() === '{') {
        this.error('Template interpolation support nathi.');
      }
      if (character !== '\\') {
        value += character;
        continue;
      }
      if (this.index >= this.source.length) this.error('String escape incomplete chhe.');
      const escaped = this.peek();
      this.index += 1;
      const simpleEscapes = {
        n: '\n',
        r: '\r',
        t: '\t',
        b: '\b',
        f: '\f',
        v: '\v',
        '0': '\0',
      };
      if (Object.hasOwn(simpleEscapes, escaped)) value += simpleEscapes[escaped];
      else if (escaped === 'u') {
        const hex = this.source.slice(this.index, this.index + 4);
        if (!/^[0-9a-fA-F]{4}$/.test(hex)) this.error('Invalid unicode escape chhe.');
        value += String.fromCharCode(Number.parseInt(hex, 16));
        this.index += 4;
      } else value += escaped;
    }
    this.error('String close thayeli nathi.');
  }

  parseNumber() {
    this.skip();
    const match = this.source.slice(this.index).match(/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/);
    if (!match) this.error('Invalid number chhe.');
    this.index += match[0].length;
    return Number(match[0]);
  }

  parseIdentifier() {
    this.skip();
    const match = this.source.slice(this.index).match(/^[A-Za-z_$][\w$]*/);
    if (!match) this.error('Field name samjayu nathi.');
    this.index += match[0].length;
    return match[0];
  }

  parseIdentifierValue() {
    const identifier = this.parseIdentifier();
    if (identifier === 'true') return true;
    if (identifier === 'false') return false;
    if (identifier === 'null' || identifier === 'undefined') return null;
    if (identifier !== 'tr') this.error(`Unsupported value/function "${identifier}".`);

    this.consume('(');
    const values = [];
    this.skip();
    while (this.peek() !== ')') {
      values.push(this.parseValue());
      this.skip();
      if (this.peek() === ',') {
        this.index += 1;
        this.skip();
        if (this.peek() === ')') break;
      } else if (this.peek() !== ')') {
        this.error('tr() value pachhi comma required chhe.');
      }
    }
    this.consume(')');
    if (values.length > 3) this.error('tr() ma maximum 3 values hoi shake.');
    return {
      gu: String(values[0] ?? ''),
      hi: String(values[1] ?? ''),
      en: String(values[2] ?? ''),
    };
  }
}

export function parsePortalDataCode(source) {
  return new PortalCodeParser(source).parse();
}

function emptyFieldValue(field) {
  if (field.type === 'translated' || (field.type === 'category' && field.storage === 'translated')) {
    return { gu: '', hi: '', en: '' };
  }
  if (field.type === 'list') return [];
  if (field.type === 'toggle') return false;
  if (field.type === 'number') return '';
  return '';
}

function normalizeTranslated(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      gu: String(value.gu ?? ''),
      hi: String(value.hi ?? ''),
      en: String(value.en ?? ''),
    };
  }
  return { gu: '', hi: '', en: value == null ? '' : String(value) };
}

function normalizeField(field, value, warnings, path) {
  if (value === undefined || value === null) return emptyFieldValue(field);
  if (field.type === 'translated' || (field.type === 'category' && field.storage === 'translated')) {
    return normalizeTranslated(value);
  }
  if (field.type === 'toggle') return value === true || value === 'true';
  if (field.type === 'number') {
    if (value === '') return '';
    const number = Number(value);
    if (!Number.isFinite(number)) {
      warnings.push(`${path} valid number nathi; blank rakhyu chhe.`);
      return '';
    }
    return number;
  }
  if (field.type === 'list') {
    if (!Array.isArray(value)) {
      warnings.push(`${path} array nathi; blank list rakhyu chhe.`);
      return [];
    }
    if (field.primitive) return value.map((item) => item == null ? '' : String(item));
    return value.map((item, index) => {
      const source = item && typeof item === 'object' && !Array.isArray(item) ? item : {};
      if (source !== item) warnings.push(`${path}[${index}] object nathi; blank item banavyo chhe.`);
      return normalizeFields(field.itemFields || [], source, warnings, `${path}[${index}]`);
    });
  }
  return String(value);
}

function normalizeFields(fields, source, warnings, path = '') {
  const allowed = new Set(fields.map((field) => field.key));
  Object.keys(source || {}).forEach((key) => {
    if (!allowed.has(key) && !SYSTEM_KEYS.has(key) && !INTERNAL_KEYS.has(key)) {
      warnings.push(`${path ? `${path}.` : ''}${key} current form ma nathi, etle ignore karyu.`);
    }
  });

  return Object.fromEntries(fields.map((field) => [
    field.key,
    normalizeField(field, source?.[field.key], warnings, path ? `${path}.${field.key}` : field.key),
  ]));
}

export function convertPortalDataToRecord(module, parsed, options = {}) {
  const warnings = [];
  const currentRecord = options.currentRecord || {};
  const protectedId = String(options.documentId || currentRecord.id || '').trim();
  const importedId = String(parsed?.id || '').trim();
  if (protectedId && importedId && importedId !== protectedId) {
    warnings.push(`Imported ID ${importedId} ignore karyo; current document ID ${protectedId} j rakhyo.`);
  }

  const fields = normalizeFields(module.fields || [], parsed || {}, warnings);
  const sortOrderValue = parsed?.sortOrder ?? currentRecord.sortOrder ?? '';
  const sortOrder = sortOrderValue === '' || sortOrderValue == null ? '' : Number(sortOrderValue);
  if (sortOrder !== '' && !Number.isFinite(sortOrder)) {
    warnings.push('sortOrder valid number nathi; blank rakhyu chhe.');
  }

  return {
    record: {
      id: protectedId || importedId,
      isActive: parsed?.isActive === undefined
        ? currentRecord.isActive !== false
        : parsed.isActive !== false,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : '',
      ...fields,
    },
    warnings,
  };
}

function indent(level) {
  return '  '.repeat(level);
}

function keyText(key) {
  return /^[A-Za-z_$][\w$]*$/.test(key) ? key : JSON.stringify(key);
}

function stringText(value) {
  return JSON.stringify(value == null ? '' : String(value));
}

function formatTranslated(value, level) {
  const translated = normalizeTranslated(value);
  const inner = indent(level + 1);
  return `tr(\n${inner}${stringText(translated.gu)},\n${inner}${stringText(translated.hi)},\n${inner}${stringText(translated.en)},\n${indent(level)})`;
}

function formatPrimitive(value) {
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  if (value === null) return 'null';
  return stringText(value);
}

function formatObjectFields(fields, value, level) {
  if (!fields.length) return '{}';
  const lines = fields.map((field) => {
    const formatted = formatFieldValue(field, value?.[field.key], level + 1);
    return `${indent(level + 1)}${keyText(field.key)}: ${formatted},`;
  });
  return `{\n${lines.join('\n\n')}\n${indent(level)}}`;
}

function formatList(field, value, level) {
  const items = Array.isArray(value) ? value : [];
  if (!items.length) return '[]';
  const lines = items.map((item) => {
    const formatted = field.primitive
      ? formatPrimitive(item)
      : formatObjectFields(field.itemFields || [], item, level + 1);
    return `${indent(level + 1)}${formatted},`;
  });
  return `[\n${lines.join('\n\n')}\n${indent(level)}]`;
}

function formatFieldValue(field, value, level) {
  if (field.type === 'translated' || (field.type === 'category' && field.storage === 'translated')) {
    return formatTranslated(value, level);
  }
  if (field.type === 'list') return formatList(field, value, level);
  if (field.type === 'toggle') return value === true ? 'true' : 'false';
  if (field.type === 'number') {
    return value === '' || value == null || !Number.isFinite(Number(value))
      ? '""'
      : String(Number(value));
  }
  return stringText(value);
}

export function formatPortalDataCode(module, record, options = {}) {
  const id = String(options.documentId || record?.id || '').trim();
  const entries = [
    { key: 'id', value: stringText(id) },
    ...(module.fields || []).map((field) => ({
      key: field.key,
      value: formatFieldValue(field, record?.[field.key], 1),
    })),
  ];

  if (record?.sortOrder !== ''
    && record?.sortOrder != null
    && Number.isFinite(Number(record.sortOrder))) {
    entries.push({ key: 'sortOrder', value: String(Number(record.sortOrder)) });
  }
  entries.push({ key: 'isActive', value: record?.isActive === false ? 'false' : 'true' });

  return `{\n${entries.map((entry) => `${indent(1)}${keyText(entry.key)}: ${entry.value},`).join('\n\n')}\n}`;
}
